// src/components/Home/WhyAlternativeProtein.jsx - MOBILE OPTIMIZED
import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Droplet, TreePine, BarChart3, Dna, Play, Pause, Volume2, VolumeX, Loader2, Smartphone } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const WhyAlternativeProtein = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16/9);

  // Video paths
  const videoSources = {
    mp4: '/assets/videos/dna-background.mp4',
    webm: `${process.env.PUBLIC_URL}/assets/videos/dna-background.webm`,
    poster: '/assets/images/placeholders/dna-poster.jpg'
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get video aspect ratio
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const aspect = video.videoWidth / video.videoHeight;
      setVideoAspectRatio(aspect || 16/9);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoaded = () => {
      setIsLoading(false);
      // Auto-play with mobile optimization
      video.volume = isMobile ? 0 : 0.3;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Auto-play prevented:', error);
          setIsPlaying(false);
        });
      }
    };

    const handleError = () => {
      console.error('Failed to load video');
      setHasError(true);
      setIsLoading(false);
    };

    video.addEventListener('loadeddata', handleLoaded);
    video.addEventListener('canplay', handleLoaded);
    video.addEventListener('error', handleError);

    // Start loading the video
    video.load();

    // GSAP animations with mobile adjustments
    const ctx = gsap.context(() => {
      // Adjust animation values based on mobile
      const titleY = isMobile ? 40 : 50;
      const cardY = isMobile ? 40 : 60;
      const cardScale = isMobile ? 0.95 : 0.9;

      // Animate title
      gsap.fromTo('.section-title', {
        y: titleY,
        opacity: 0,
      }, {
        y: 0,
        opacity: 1,
        duration: isMobile ? 0.8 : 1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
      });

      // Animate cards with mobile optimization
      gsap.fromTo('.challenge-card', {
        y: cardY,
        opacity: 0,
        scale: cardScale,
      }, {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: isMobile ? 0.6 : 0.8,
        stagger: isMobile ? 0.15 : 0.2,
        ease: 'back.out(1.2)',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%',
          toggleActions: 'play none none reverse',
        },
      });

      // Animate DNA icon
      gsap.fromTo('.dna-icon', {
        rotation: -180,
        scale: 0,
      }, {
        rotation: 0,
        scale: 1,
        duration: 1.5,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });

    }, sectionRef);

    return () => {
      if (video) {
        video.removeEventListener('loadeddata', handleLoaded);
        video.removeEventListener('canplay', handleLoaded);
        video.removeEventListener('error', handleError);
        video.pause();
      }
      ctx.revert();
    };
  }, [isMobile]);

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      gsap.to(videoRef.current, { opacity: 0.7, duration: 0.3 });
    } else {
      videoRef.current.play().catch(e => {
        console.log('Play error:', e);
      });
      gsap.to(videoRef.current, { opacity: 1, duration: 0.3 });
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Calculate video container size for mobile
  const getVideoContainerStyle = () => {
    if (!isMobile) return {};
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const videoHeight = viewportWidth / videoAspectRatio;
    
    if (videoHeight > viewportHeight * 0.6) {
      // Scale to fit height
      const scale = (viewportHeight * 0.6) / videoHeight;
      return {
        width: `${viewportWidth * scale}px`,
        height: `${viewportHeight * 0.6}px`,
        margin: '0 auto'
      };
    }
    
    return {
      width: '100%',
      height: `${videoHeight}px`,
      margin: '0 auto'
    };
  };

  const challenges = [
    {
      icon: TreePine,
      title: 'Land Availability',
      description: 'Shrinking arable land for traditional agriculture',
      color: 'from-red-500 to-orange-500',
      stat: '80% less land'
    },
    {
      icon: Droplet,
      title: 'Water Stress',
      description: 'Intensifying global water scarcity issues',
      color: 'from-blue-500 to-cyan-500',
      stat: '90% water savings'
    },
    {
      icon: AlertTriangle,
      title: 'Environmental Costs',
      description: 'Rising ecological impact of conventional methods',
      color: 'from-yellow-500 to-amber-500',
      stat: '85% lower emissions'
    },
    {
      icon: BarChart3,
      title: 'Supply Volatility',
      description: 'Increasing instability in protein supply chains',
      color: 'from-purple-500 to-pink-500',
      stat: 'Year-round production'
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-12 md:py-20 overflow-hidden bg-black">
      {/* Mobile Indicator */}
      {/* {isMobile && process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 right-4 z-50">
          <div className="px-2 py-1 bg-black/80 backdrop-blur-sm border border-gold-500/30 rounded-full flex items-center space-x-1">
            <Smartphone className="w-3 h-3 text-gold-400" />
            <span className="text-xs text-gold-300">Mobile</span>
          </div>
        </div>
      )} */}

      {/* Video Background Container */}
      <div className="absolute inset-0 z-0 bg-black">
        {hasError ? (
          // Fallback gradient if video fails
          <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
            <div className="absolute inset-0 flex items-center justify-center opacity-20">
              <Dna className="w-48 md:w-64 h-48 md:h-64 text-gold-500/30" />
            </div>
          </div>
        ) : (
          <>
            {/* Video Container - Different for mobile vs desktop */}
            <div 
              ref={videoContainerRef}
              className={`absolute ${
                isMobile 
                  ? 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' 
                  : 'inset-0'
              }`}
              style={isMobile ? getVideoContainerStyle() : {}}
            >
              <video
                ref={videoRef}
                className={`absolute ${
                  isMobile 
                    ? 'w-full h-full object-contain'  // Contain for mobile
                    : 'inset-0 w-full h-full object-cover'  // Cover for desktop
                }`}
                muted={isMuted}
                loop
                playsInline
                preload="auto"
                poster={videoSources.poster}
                style={{ backgroundColor: '#000000' }}
              >
                <source src={videoSources.webm} type="video/webm" />
                <source src={videoSources.mp4} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center">
                  <div className="relative w-16 h-16 md:w-20 md:h-20 mb-4">
                    <div className="absolute inset-0 border-3 md:border-4 border-gold-500/30 rounded-full"></div>
                    <div className="absolute inset-3 md:inset-4 border-3 md:border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gold-300 text-base md:text-lg">Loading DNA visualization...</p>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Overlay gradients - Mobile optimized */}
        {isMobile ? (
          // Mobile: Simple gradients
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/90" />
            <div className="absolute top-0 left-0 right-0 h-1/4 bg-gradient-to-b from-black to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black to-transparent" />
          </>
        ) : (
          // Desktop: Full gradients
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-dark-900/85 via-dark-900/50 to-dark-900/85" />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-900/40 via-transparent to-dark-900/40" />
          </>
        )}
        
        {/* Animated particles overlay - Reduced on mobile */}
        <div className="absolute inset-0">
          {[...Array(isMobile ? 10 : 20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-[1px] md:w-[2px] h-[1px] md:h-[2px] bg-gold-400/30 md:bg-gold-400/40 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 4}s`,
                boxShadow: isMobile 
                  ? '0 0 5px 1px rgba(212, 175, 55, 0.3)'
                  : '0 0 10px 2px rgba(212, 175, 55, 0.5)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Content Layer */}
      <div className="relative z-10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {/* Header Section - Mobile optimized */}
            <div className="text-center mb-12 md:mb-20">
              <div className="dna-icon inline-flex items-center justify-center mb-6 md:mb-8">
                <div className="relative">
                  <div className={`absolute -inset-${isMobile ? '3' : '4'} border border-gold-500/20 rounded-full animate-spin-slow`}></div>
                </div>
              </div>
              
              <h1 className="section-title text-3xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6">
                <span className="gold-gradient">The Biological Blueprint</span>
                <br />
                <span className="text-white">for Future Protein</span>
              </h1>
              
              <div className={`w-24 md:w-32 h-0.5 md:h-1 bg-gradient-to-r from-gold-400 to-gold-600 mx-auto mb-6 md:mb-8`} />
              
              <p className="text-lg md:text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light px-2">
                Traditional protein systems are reaching biological limits. We're decoding nature's 
                most efficient molecular pathways to build sustainable, climate-independent nutrition.
              </p>
            </div>

            {/* Challenge Cards - Mobile responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-20">
              {challenges.map((challenge, index) => (
                <div key={index} className="challenge-card group">
                  <div className="h-full p-4 md:p-6 bg-dark-900/80 md:bg-dark-900/70 backdrop-blur-sm rounded-xl md:rounded-2xl border border-gold-500/20 hover:border-gold-500/40 transition-all duration-500 hover:shadow-xl md:hover:shadow-2xl hover:shadow-gold-500/20">
                    <div className={`relative w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-gradient-to-br ${challenge.color} flex items-center justify-center mb-4 md:mb-6 group-hover:scale-105 md:group-hover:scale-110 transition-transform duration-300`}>
                      <challenge.icon className="w-5 h-5 md:w-7 md:h-7 text-white" />
                      <div className="absolute -inset-1 md:-inset-2 border border-white/20 rounded-lg md:rounded-xl animate-pulse"></div>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3 group-hover:text-gold-300 transition-colors">
                      {challenge.title}
                    </h3>
                    <p className="text-gray-400 text-sm md:text-base mb-3 md:mb-4 group-hover:text-gray-300 transition-colors">
                      {challenge.description}
                    </p>
                    <div className="text-gold-400 text-xs md:text-sm font-semibold">
                      {challenge.stat}
                    </div>
                    
                    {/* Animated progress indicator */}
                    <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gold-500/20 group-hover:border-gold-500/40 transition-colors">
                      <div className="w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-gold-500 to-transparent transition-all duration-500"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Scientific Insight Box - Mobile responsive */}
            <div className="bg-dark-900/80 md:bg-dark-900/70 backdrop-blur-sm rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-12 border border-gold-500/20 shadow-xl md:shadow-2xl overflow-hidden">
              <div className="relative">
                {/* Animated background element - Smaller on mobile */}
                <div className={`absolute ${
                  isMobile ? '-right-6 -top-6 w-24 h-24' : '-right-10 -top-10 w-40 h-40'
                } opacity-10`}>
                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 border border-gold-500/30 rounded-full animate-spin-slow"></div>
                    <div className={`absolute ${
                      isMobile ? 'inset-6' : 'inset-10'
                    } border border-gold-500/20 rounded-full animate-spin-slow`} style={{ animationDirection: 'reverse' }}></div>
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 md:w-20 lg:w-24 ${
                      isMobile ? 'mx-auto' : ''
                    } rounded-full bg-gradient-to-br from-gold-600/20 to-gold-800/10 border-2 border-gold-500/30 flex items-center justify-center`}>
                      <span className="text-3xl md:text-4xl">🧬</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-lg md:text-xl lg:text-2xl text-gray-300 italic leading-relaxed mb-4 md:mb-6 text-center md:text-left">
                      "Just as DNA encodes life's most efficient processes, we're engineering 
                      biological systems that produce protein with unprecedented resource efficiency, 
                      creating a new paradigm for sustainable nutrition."
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                      {[
                        { label: 'Genetic Efficiency', value: '98%', color: 'text-green-400' },
                        { label: 'Feed Conversion', value: '2:1', color: 'text-gold-400' },
                        { label: 'Water Savings', value: '90%', color: 'text-blue-400' },
                        { label: 'Carbon Reduction', value: '85%', color: 'text-emerald-400' },
                      ].map((stat, index) => (
                        <div key={index} className="text-center">
                          <div className={`text-xl md:text-2xl lg:text-3xl font-bold ${stat.color} mb-1`}>
                            {stat.value}
                          </div>
                          <div className="text-xs text-gray-400 uppercase tracking-wider">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default WhyAlternativeProtein;